import api from "@/lib/api/axios";

export type DigestFrequencyEnum = "daily" | "weekly" | "never";

export interface Notification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  read: boolean;
  action_url: string;
  data: {
    name?: string;
    avatar?: string;
    lender?: string;
    item?: string;
    [key: string]: string | number | boolean | undefined;
  } | null;
  created_at: string;
  scheduled_for: string | null;
  sent_at: string | null;
  read_at: string | null;
}

export interface NotificationSettings {
  email_notifications: boolean;
  push_notifications: boolean;
  web_notifications: boolean;
  email_digest: boolean;
  digest_frequency: DigestFrequencyEnum;
  notification_types: {
    messages?: boolean;
    mentions?: boolean;
    comments?: boolean;
    likes?: boolean;
    follows?: boolean;
    system?: boolean;
    [key: string]: boolean | undefined;
  };
}

export interface UnreadCount {
  unread_count: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface NotificationListParams {
  page?: number;
  ordering?: string;
  search?: string;
}

// Mock data for when API calls fail
const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: "1",
    title: "Bình luận mới",
    message: "đã bình luận về bài viết của bạn",
    notification_type: "comment",
    read: false,
    action_url: "/posts/1",
    data: {
      name: "Marnie",
      avatar: "/avatars/marnie.svg"
    },
    created_at: new Date().toISOString(),
    scheduled_for: null,
    sent_at: null,
    read_at: null
  },
  {
    id: "2",
    title: "Lượt thích mới",
    message: "đã thích bài viết của bạn",
    notification_type: "like",
    read: false,
    action_url: "/posts/2",
    data: {
      name: "Haku",
      avatar: "/avatars/haku.svg"
    },
    created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
    scheduled_for: null,
    sent_at: null,
    read_at: null
  },
  {
    id: "3",
    title: "Bình luận mới",
    message: "đã bình luận về bài viết của bạn",
    notification_type: "comment",
    read: false,
    action_url: "/posts/1",
    data: {
      name: "Kiki",
      avatar: "/avatars/kiki.svg"
    },
    created_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(), // 1 hour ago
    scheduled_for: null,
    sent_at: null,
    read_at: null
  },
  {
    id: "4",
    title: "Bình luận mới",
    message: "đã bình luận về bài viết của bạn",
    notification_type: "comment",
    read: true,
    action_url: "/posts/3",
    data: {
      name: "Ponyo",
      avatar: "/avatars/ponyo.svg"
    },
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
    scheduled_for: null,
    sent_at: null,
    read_at: null
  },
  {
    id: "5",
    title: "Bình luận mới",
    message: "đã bình luận về bài viết của bạn",
    notification_type: "comment",
    read: true,
    action_url: "/posts/2",
    data: {
      name: "Kiki",
      avatar: "/avatars/kiki.svg"
    },
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(), // 2 days ago
    scheduled_for: null,
    sent_at: null,
    read_at: null
  }
];

const MOCK_NOTIFICATION_SETTINGS: NotificationSettings = {
  email_notifications: true,
  push_notifications: true,
  web_notifications: true,
  email_digest: true,
  digest_frequency: "daily",
  notification_types: {
    messages: true,
    mentions: true,
    comments: true,
    likes: true,
    follows: true,
    system: true
  }
};

export const notificationsApi = {
  /**
   * Get list of notifications for the current user
   */
  getNotifications: async (params?: NotificationListParams): Promise<PaginatedResponse<Notification>> => {
    try {
      const response = await api.get<PaginatedResponse<Notification>>("/notifications/", { params });
      return response.data;
    } catch (error) {
      console.log("Using mock notifications due to API error:", error);
      // Return mock data when the API call fails
      const page = params?.page || 1;
      const pageSize = 10;
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedResults = MOCK_NOTIFICATIONS.slice(startIndex, endIndex);
      
      return {
        count: MOCK_NOTIFICATIONS.length,
        next: endIndex < MOCK_NOTIFICATIONS.length ? `?page=${page + 1}` : null,
        previous: page > 1 ? `?page=${page - 1}` : null,
        results: paginatedResults
      };
    }
  },

  /**
   * Get a single notification by ID
   */
  getNotification: async (id: string): Promise<Notification> => {
    try {
      const response = await api.get<Notification>(`/notifications/${id}/`);
      return response.data;
    } catch (error) {
      console.log("Using mock notification due to API error:", error);
      // Return mock data when the API call fails
      const notification = MOCK_NOTIFICATIONS.find(n => n.id === id);
      if (!notification) {
        throw new Error(`Notification with ID ${id} not found`);
      }
      return notification;
    }
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (id: string): Promise<void> => {
    try {
      await api.delete(`/notifications/${id}/`);
    } catch (error) {
      console.log("Mock delete notification due to API error:", error);
      // Do nothing in mock mode
    }
  },

  /**
   * Get user's notification preferences
   */
  getNotificationPreferences: async (): Promise<NotificationSettings> => {
    try {
      const response = await api.get<NotificationSettings>("/notifications/preferences/");
      return response.data;
    } catch (error) {
      console.log("Using mock notification preferences due to API error:", error);
      // Return mock data when the API call fails
      return MOCK_NOTIFICATION_SETTINGS;
    }
  },

  /**
   * Update user's notification preferences (full update)
   */
  updateNotificationPreferences: async (data: NotificationSettings): Promise<NotificationSettings> => {
    try {
      const response = await api.put<NotificationSettings>("/notifications/preferences/", data);
      return response.data;
    } catch (error) {
      console.log("Using mock update notification preferences due to API error:", error);
      // Return the data that was passed in as if it was saved
      return data;
    }
  },

  /**
   * Partially update user's notification preferences
   */
  patchNotificationPreferences: async (data: Partial<NotificationSettings>): Promise<NotificationSettings> => {
    try {
      const response = await api.patch<NotificationSettings>("/notifications/preferences/", data);
      return response.data;
    } catch (error) {
      console.log("Using mock patch notification preferences due to API error:", error);
      // Return the data that was passed in as if it was saved
      return { ...MOCK_NOTIFICATION_SETTINGS, ...data };
    }
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (id: string): Promise<Notification> => {
    try {
      const response = await api.post<Notification>(`/notifications/${id}/read/`);
      return response.data;
    } catch (error) {
      console.log("Using mock mark as read due to API error:", error);
      // Return mock data when the API call fails
      const notification = MOCK_NOTIFICATIONS.find(n => n.id === id);
      if (!notification) {
        throw new Error(`Notification with ID ${id} not found`);
      }
      return { ...notification, read: true, read_at: new Date().toISOString() };
    }
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<void> => {
    try {
      await api.post("/notifications/read-all/");
    } catch (error) {
      console.log("Mock mark all as read due to API error:", error);
      // Do nothing in mock mode
    }
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (): Promise<UnreadCount> => {
    try {
      const response = await api.get<UnreadCount>("/notifications/unread-count/");
      return response.data;
    } catch (error) {
      console.log("Using mock unread count due to API error:", error);
      // Return mock data when the API call fails
      return {
        unread_count: MOCK_NOTIFICATIONS.filter(n => !n.read).length
      };
    }
  }
}; 